import type { BatchCreateRolloutWindowsRequest } from '@emma/api_client/types';
import { z } from 'zod';

const plannedRolloutWindowSchema = z.strictObject({
  deviceId: z.number().int(),
  durationInSeconds: z.number().int().positive(),
  idempotencyKey: z.uuid(),
  startTime: z.iso.datetime(),
  type: z.enum(['APPLICATION', 'APPLICATION_AND_OS']).optional(),
});

const pendingSubmissionSchema = z.strictObject({
  tag: z.string().min(1),
  rolloutWindows: z.array(plannedRolloutWindowSchema).min(1),
});

export type PlannedRolloutWindow = BatchCreateRolloutWindowsRequest['rolloutWindows'][number];
export type PendingSubmission = {
  tag: string;
  rolloutWindows: PlannedRolloutWindow[];
};

export const pendingSubmissionStorageKey = (
  workspaceId: number,
  fleetId: number,
  deploymentId: number
): string => `gradual-rollout:${workspaceId}:${fleetId}:${deploymentId}`;

export const loadPendingSubmission = (
  workspaceId: number,
  fleetId: number,
  deploymentId: number
): PendingSubmission | undefined => {
  const key = pendingSubmissionStorageKey(workspaceId, fleetId, deploymentId);

  try {
    const stored = sessionStorage.getItem(key);
    if (!stored) return undefined;

    const parsed = pendingSubmissionSchema.safeParse(JSON.parse(stored));
    if (parsed.success) return parsed.data;

    sessionStorage.removeItem(key);
    return undefined;
  } catch {
    return undefined;
  }
};

export const savePendingSubmission = (
  workspaceId: number,
  fleetId: number,
  deploymentId: number,
  submission: PendingSubmission
): void => {
  const parsed = pendingSubmissionSchema.parse(submission);
  sessionStorage.setItem(
    pendingSubmissionStorageKey(workspaceId, fleetId, deploymentId),
    JSON.stringify(parsed)
  );
};

export const clearPendingSubmission = (
  workspaceId: number,
  fleetId: number,
  deploymentId: number
): void => {
  sessionStorage.removeItem(pendingSubmissionStorageKey(workspaceId, fleetId, deploymentId));
};
